import { useState, useEffect } from 'react'
import { api } from '../utils/api'
import { useEditorStore } from '../store/editor.store'

// ── §35+§74: Version History with Diff View ───────────────────────────────────

interface PageVersion {
  id: string
  version: number
  title: string
  seoTitle?: string
  seoDesc?: string
  createdAt: string
}

interface VersionContent {
  id: string
  version: number
  title: string
  content: { sections?: Array<{ id: string; blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }> }
}

interface DiffResult {
  added: Array<{ sectionIdx: number; type: string }>
  removed: Array<{ sectionIdx: number; type: string }>
  changed: Array<{ id: string; type: string; changedFields: string[] }>
  sectionsAdded: number
  sectionsRemoved: number
}

function computeDiff(currentSections: Array<{ blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }>, versionContent: VersionContent['content']): DiffResult {
  const versionSections = versionContent?.sections ?? []
  const currentBlocks = new Map(currentSections.flatMap(s => s.blocks).map(b => [b.id, b]))
  const versionBlocks = new Map(versionSections.flatMap(s => s.blocks).map(b => [b.id, b]))

  const added: DiffResult['added'] = []
  const removed: DiffResult['removed'] = []
  const changed: DiffResult['changed'] = []

  // Blocks in current not in version → added since that version
  for (const [id, blk] of currentBlocks) {
    if (!versionBlocks.has(id)) {
      added.push({ sectionIdx: 0, type: blk.type })
    }
  }

  // Blocks in version not in current → removed since that version
  for (const [id, blk] of versionBlocks) {
    if (!currentBlocks.has(id)) {
      removed.push({ sectionIdx: 0, type: blk.type })
    }
  }

  // Blocks in both → check for changes
  for (const [id, vBlk] of versionBlocks) {
    const cBlk = currentBlocks.get(id)
    if (!cBlk) continue
    const changedFields: string[] = []
    for (const [key, val] of Object.entries(vBlk.data)) {
      if (typeof val === 'string' && typeof cBlk.data[key] === 'string' && cBlk.data[key] !== val) {
        changedFields.push(key)
      }
    }
    if (changedFields.length > 0) changed.push({ id, type: cBlk.type, changedFields })
  }

  return {
    added, removed, changed,
    sectionsAdded: Math.max(0, currentSections.length - versionSections.length),
    sectionsRemoved: Math.max(0, versionSections.length - currentSections.length),
  }
}

function DiffView({ diff, onClose }: { diff: DiffResult & { versionNum: number }; onClose: () => void }) {
  const totalChanges = diff.added.length + diff.removed.length + diff.changed.length
  return (
    <div style={{ padding: '16px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
          Версия {diff.versionNum} → текущая
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer' }}>← Назад</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, padding: '12px', background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e' }}>+{diff.added.length} блоков</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#ef4444' }}>−{diff.removed.length} блоков</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6' }}>✏ {diff.changed.length} изменено</span>
        {diff.sectionsAdded > 0 && <span style={{ fontSize: 12, color: '#22c55e' }}>+{diff.sectionsAdded} секций</span>}
        {diff.sectionsRemoved > 0 && <span style={{ fontSize: 12, color: '#ef4444' }}>−{diff.sectionsRemoved} секций</span>}
        {totalChanges === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Изменений нет</span>}
      </div>

      {/* Added blocks */}
      {diff.added.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Добавлено ({diff.added.length})
          </div>
          {diff.added.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, marginBottom: 4, fontSize: 12 }}>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>+</span>
              <span style={{ color: '#166534' }}>{b.type}</span>
            </div>
          ))}
        </div>
      )}

      {/* Removed blocks */}
      {diff.removed.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Удалено ({diff.removed.length})
          </div>
          {diff.removed.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 7, marginBottom: 4, fontSize: 12 }}>
              <span style={{ color: '#ef4444', fontWeight: 700 }}>−</span>
              <span style={{ color: '#991b1b' }}>{b.type}</span>
            </div>
          ))}
        </div>
      )}

      {/* Changed blocks */}
      {diff.changed.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Изменено ({diff.changed.length})
          </div>
          {diff.changed.map((b, i) => (
            <div key={i} style={{ padding: '6px 10px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 7, marginBottom: 4 }}>
              <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>{b.type}</div>
              <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
                {b.changedFields.slice(0, 5).join(', ')}{b.changedFields.length > 5 ? ` +${b.changedFields.length - 5}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface Props {
  siteId: string | null
  onClose: () => void
}

export function VersionHistory({ siteId, onClose }: Props) {
  const [versions, setVersions] = useState<PageVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [restoring, setRestoring] = useState<string | null>(null)
  const [diffLoading, setDiffLoading] = useState<string | null>(null)
  const [diff, setDiff] = useState<(DiffResult & { versionNum: number }) | null>(null)
  const [error, setError] = useState('')
  const { page } = useEditorStore()

  const pageSlug = encodeURIComponent(page?.slug ?? '/')

  useEffect(() => {
    if (!siteId) return
    setLoading(true)
    api.get<PageVersion[]>(`/sites/${siteId}/history?slug=${pageSlug}`)
      .then(setVersions)
      .catch(() => setVersions([]))
      .finally(() => setLoading(false))
  }, [siteId, pageSlug])

  async function restore(ver: PageVersion) {
    if (!siteId) return
    if (!confirm(`Восстановить страницу до версии ${ver.version} от ${new Date(ver.createdAt).toLocaleString('ru')}?`)) return
    setRestoring(ver.id)
    setError('')
    try {
      const restored = await api.post<{ title: string; seoTitle?: string; seoDesc?: string; content: unknown }>(
        `/sites/${siteId}/history/${ver.id}/restore?slug=${pageSlug}`, {}
      )
      const store = useEditorStore.getState()
      store.updatePage({ title: restored.title, seoTitle: restored.seoTitle, seoDesc: restored.seoDesc })
      const content = restored.content as { sections?: unknown[] }
      if (content?.sections && Array.isArray(content.sections)) {
        for (const sec of [...store.page.sections]) store.removeSection(sec.id)
        const sections = content.sections as Array<{ id: string; blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }>
        for (const sec of sections) {
          store.addSection()
          const added = useEditorStore.getState().page.sections.at(-1)
          if (added) {
            for (const b of sec.blocks) store.addBlock(added.id, b.type, b.data)
          }
        }
      }
      onClose()
    } catch (e) { setError(String(e)) }
    finally { setRestoring(null) }
  }

  async function compare(ver: PageVersion) {
    if (!siteId) return
    setDiffLoading(ver.id)
    try {
      const verContent = await api.get<VersionContent>(`/sites/${siteId}/history/${ver.id}`)
      const currentSections = useEditorStore.getState().page.sections
      const d = computeDiff(currentSections, verContent.content)
      setDiff({ ...d, versionNum: ver.version })
    } catch { setError('Не удалось загрузить версию для сравнения') }
    finally { setDiffLoading(null) }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ width: '420px', maxWidth: '95vw', height: '100vh', background: 'var(--bg-1)', borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'var(--bg-1)' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
              {diff ? '🔀 Diff' : '📋 История версий'}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{page.title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {error && (
            <div style={{ margin: '16px 24px', padding: '10px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', fontSize: '12px' }}>
              {error}
            </div>
          )}

          {diff ? (
            <DiffView diff={diff} onClose={() => setDiff(null)} />
          ) : (
            <div style={{ padding: '16px 24px' }}>
              {loading && <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Загрузка...</div>}

              {!loading && versions.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    История пуста.<br />Версии создаются при каждой публикации сайта.
                  </div>
                </div>
              )}

              {!loading && versions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {versions.map((ver) => (
                    <div
                      key={ver.id}
                      style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                            Версия {ver.version}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {new Date(ver.createdAt).toLocaleString('ru', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {ver.seoTitle && (
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ver.seoTitle}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                          <button
                            onClick={() => void compare(ver)}
                            disabled={diffLoading === ver.id}
                            style={{ padding: '5px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: 'var(--text-secondary)', opacity: diffLoading === ver.id ? 0.7 : 1 }}
                          >
                            {diffLoading === ver.id ? '...' : '🔀 Diff'}
                          </button>
                          <button
                            onClick={() => void restore(ver)}
                            disabled={restoring === ver.id}
                            style={{ padding: '5px 10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', opacity: restoring === ver.id ? 0.7 : 1 }}
                          >
                            {restoring === ver.id ? '...' : 'Восстановить'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
